import * as React from "react";
import { Button } from "../actions/Button";
import { IconButton } from "../actions/IconButton";
import { Logo } from "../brand/Logo";
import { Meter } from "../display/Meter";
import { formatHotkey } from "../../lib/hotkeys";
import { NavItem } from "./NavItem";
import { WorkspaceSwitcher, type WorkspaceMenuItem } from "./WorkspaceSwitcher";

export interface SidebarFolderItem {
  id: string;
  label: string;
  color: string;
  count?: string;
  /** Nesting depth, 0 = top level (F2: folders nest up to 3 deep). Default 0. */
  depth?: number;
  /** Shown as a small heading above this item, e.g. "Personal" — set on the first item of a
   * new scope group so the list reads as Personal / Team / Org rather than one flat list. */
  scopeLabel?: string;
}

/**
 * Desktop app / desktop web nav rail (DS NavRail): the Grackle lockup, the workspace switcher, primary
 * nav, a lavender divider, extra nav and folders, then usage, Settings and the ink "Record a video"
 * CTA pinned to the bottom. The CTA renders only when `onNewRecording` is given.
 *
 * nav and org are required and have no sample defaults: the sidebar only ever shows the
 * caller's real data (PR1 contract change, see Sidebar.prompt.md). folders and usage are
 * optional so a product without folders or quotas yet can hide those blocks entirely.
 */
export interface SidebarProps {
  active?: string;
  onNavigate?: (id: string) => void;
  /**
   * URL for a nav, folder, settings or trash id. When it returns a string the row renders as a
   * link (middle-click and copy-link work); a plain click still calls onNavigate, so a router
   * can handle it client-side.
   */
  hrefFor?: (id: string) => string | undefined;
  /** Shows the ink "Record a video" CTA and handles it. Omit it to leave the CTA out (the web
   * app does: its top bar carries Record a video). */
  onNewRecording?: () => void;
  /** Disables Record a video, e.g. until the account's email is verified */
  newRecordingDisabled?: boolean;
  /** Tooltip for Record a video, e.g. why it is disabled */
  newRecordingTitle?: string;
  /** Hotkey hint on Record a video. Default: Alt+Shift+R, formatted for the platform */
  newRecordingKbd?: string;
  /** Opens the org switcher page. Used when `orgMenu` is not given. */
  onOpenOrg?: () => void;
  /** DS WorkspaceSwitcher quick menu: other orgs (with `checked` on the current one), then actions
   * such as "Invite people" and "Workspace settings". When set, the card opens this menu instead
   * of calling `onOpenOrg`. */
  orgMenu?: WorkspaceMenuItem[];
  onOrgMenuSelect?: (id: string) => void;
  /** macOS traffic lights row. Default false: the Tauri app uses native window decorations */
  windowControls?: boolean;
  nav: { id: string; icon: string; label: string; count?: string }[];
  /** Extra navigation under the primary nav (e.g. an admin group). Rendered as given */
  extraNav?: React.ReactNode;
  /** Folders from the folders API. The Folders section, Trash included, is hidden while undefined */
  folders?: SidebarFolderItem[];
  /** Shows a small "+" next to the Folders heading, e.g. to open a create-folder dialog.
   * Hidden (no button) while undefined — same undefined-hides-it convention as `folders`. */
  onNewFolder?: () => void;
  /** Shows "My account" just above Settings and opens it (the web app's account dialog).
   * Hidden while undefined. */
  onMyAccount?: () => void;
  /** The usage block is hidden while undefined or total is 0 (quota not loaded or not applicable) */
  usage?: { used: number; total: number; label?: string };
  /** The org switcher is hidden while name is empty */
  org: { name: string; detail: string; initial: string };
  className?: string;
  id?: string;
}

/** A plain left click goes to onNavigate; modified clicks (new tab, new window) follow the link. */
function isPlainClick(e: React.MouseEvent): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

export function Sidebar({
  active = "home",
  onNavigate,
  hrefFor,
  onNewRecording,
  newRecordingDisabled,
  newRecordingTitle,
  newRecordingKbd,
  onOpenOrg,
  orgMenu,
  onOrgMenuSelect,
  windowControls = false,
  nav,
  extraNav,
  folders,
  onNewFolder,
  onMyAccount,
  usage,
  org,
  className,
  id,
}: SidebarProps): JSX.Element {
  const item = (itemId: string) => {
    const href = hrefFor?.(itemId);
    return {
      href,
      onClick: (e: React.MouseEvent) => {
        if (href && !isPlainClick(e)) return;
        if (href && onNavigate) e.preventDefault();
        onNavigate?.(itemId);
      },
    };
  };
  return (
    <aside className={["gk-sidebar", className].filter(Boolean).join(" ")} id={id}>
      {windowControls && (
        <div className="gk-sidebar__chrome">
          <span className="gk-traffic" style={{ background: "#FF5F57" }} />
          <span className="gk-traffic" style={{ background: "#FEBC2E" }} />
          <span className="gk-traffic" style={{ background: "#28C840" }} />
        </div>
      )}
      {hrefFor?.("home") ? (
        <a className="gk-sidebar__brand" aria-label="Grackle home" {...item("home")}>
          <Logo height={20} />
        </a>
      ) : (
        <div className="gk-sidebar__brand">
          <Logo height={20} />
        </div>
      )}
      {org.name ? (
        <WorkspaceSwitcher
          name={org.name}
          meta={org.detail}
          initial={org.initial}
          onClick={orgMenu ? undefined : onOpenOrg}
          menu={orgMenu}
          onMenuSelect={onOrgMenuSelect}
        />
      ) : null}
      <nav className="gk-sidebar__group">
        {nav.map((n) => (
          <NavItem key={n.id} icon={n.icon} label={n.label} count={n.count} active={active === n.id} {...item(n.id)} />
        ))}
      </nav>
      <div className="gk-sidebar__divider" role="presentation" />
      {extraNav}
      {folders && (
        <div className="gk-sidebar__group">
          <div className="gk-overline gk-sidebar__label">
            <span>Folders</span>
            {onNewFolder && <IconButton icon="plus" label="New folder" variant="ghost" size="sm" onClick={onNewFolder} />}
          </div>
          {folders.map((f) => (
            <React.Fragment key={f.id}>
              {f.scopeLabel && <div className="gk-sidebar__scope">{f.scopeLabel}</div>}
              <NavItem
                swatch={f.color}
                label={f.label}
                count={f.count}
                active={active === f.id}
                style={{ paddingLeft: 12 + (f.depth ?? 0) * 14 }}
                {...item(f.id)}
              />
            </React.Fragment>
          ))}
          <NavItem icon="trash" label="Trash" active={active === "trash"} {...item("trash")} />
        </div>
      )}
      {onMyAccount && <NavItem icon="user" label="My account" onClick={onMyAccount} />}
      <NavItem icon="gear-six" label="Settings" active={active === "settings"} {...item("settings")} />
      <div className="gk-sidebar__foot">
        {usage && usage.total > 0 && (
          <div className="gk-sidebar__usage">
            <Meter label={usage.label} valueLabel={usage.used + " / " + usage.total} value={usage.used / usage.total} />
          </div>
        )}
        {onNewRecording && (
          <Button
            variant="dark"
            size="lg"
            icon="video-camera"
            kbd={newRecordingKbd ?? formatHotkey(["Alt", "Shift", "R"])}
            block
            onClick={onNewRecording}
            disabled={newRecordingDisabled}
            title={newRecordingTitle}
            className="gk-sidebar__new"
          >
            <span className="gk-sidebar__new-label">Record a video</span>
          </Button>
        )}
      </div>
    </aside>
  );
}
